/**
 * Zustand store for application settings
 * Handles theme, display preferences, and other app-wide settings
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AppSettings, EliteAdvanceCostMode } from '../types';
import { StorageLocation } from '../types/persistence';
interface SettingsState extends AppSettings {
  // Actions
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setAutoSave: (enabled: boolean) => void;
  setAutoSaveInterval: (interval: number) => void;
  setDefaultStorageLocation: (location: StorageLocation) => void;
  setShowAdvancedOptions: (show: boolean) => void;
  setRollAnimations: (enabled: boolean) => void;
  setStartingXPBonus: (enabled: boolean) => void;
  setEliteAdvanceCostMode: (mode: EliteAdvanceCostMode) => void;
  setCompactMode: (enabled: boolean) => void;
  setEnforceEncumbrance: (enabled: boolean) => void;
  setFixedStartingFatePoints: (enabled: boolean) => void;
  resetToDefaults: () => void;

  // Internal actions
  _applyTheme: () => void;
}

const DEFAULT_SETTINGS: AppSettings = {
  theme: 'system',
  autoSave: true,
  autoSaveInterval: 500,
  enabledRulebooks: ['rogue-trader-core'],
  defaultStorageLocation: StorageLocation.Local,
  showAdvancedOptions: false,
  rollAnimations: true,
  startingXPBonus: true,
  eliteAdvanceCostMode: 'double',
  compactMode: true,
  enforceEncumbrance: true,
  fixedStartingFatePoints: false
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      // Initial state from defaults
      ...DEFAULT_SETTINGS,

      // Theme management
      setTheme: (theme) => {
        console.log('🎨 Setting theme to:', theme);
        set({ theme });
        get()._applyTheme();
        console.log(
          '🎨 Theme applied. Dark class present:',
          document.documentElement.classList.contains('dark')
        );
      },

      // Auto-save settings
      setAutoSave: (autoSave) => {
        set({ autoSave });
      },

      setAutoSaveInterval: (autoSaveInterval) => {
        set({ autoSaveInterval });
      },

      // Storage settings
      setDefaultStorageLocation: (defaultStorageLocation) => {
        set({ defaultStorageLocation });
      },

      // Display preferences
      setShowAdvancedOptions: (showAdvancedOptions) => {
        set({ showAdvancedOptions });
      },

      setRollAnimations: (rollAnimations) => {
        set({ rollAnimations });
      },

      // Starting XP bonus setting
      setStartingXPBonus: (startingXPBonus) => {
        set({ startingXPBonus });
      },

      // Elite advance cost mode
      setEliteAdvanceCostMode: (eliteAdvanceCostMode) => {
        set({ eliteAdvanceCostMode });
      },

      // Compact mode
      setCompactMode: (compactMode) => {
        set({ compactMode });
      },

      // Encumbrance enforcement (home rule)
      setEnforceEncumbrance: (enforceEncumbrance) => {
        set({ enforceEncumbrance });
      },

      // Flat starting Fate Points (home rule)
      setFixedStartingFatePoints: (fixedStartingFatePoints) => {
        set({ fixedStartingFatePoints });
      },

      // Reset to defaults
      resetToDefaults: () => {
        set(DEFAULT_SETTINGS);
        get()._applyTheme();
      },

      // Apply theme to document
      _applyTheme: () => {
        const { theme } = get();
        const root = document.documentElement;

        // Remove dark class first to ensure clean state
        root.classList.remove('dark');

        if (theme === 'system') {
          // Use system preference
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          if (prefersDark) {
            root.classList.add('dark');
          }
        } else if (theme === 'dark') {
          // Use explicit dark theme
          root.classList.add('dark');
        }
        // For 'light' theme, dark class stays removed

        // Force a reflow to ensure Chrome applies the changes
        void root.offsetHeight;
      }
    }),
    {
      name: 'rogue-trader-settings',
      version: 1
    }
  )
);

// Initialize theme on store creation
if (typeof window !== 'undefined') {
  // Apply theme immediately
  useSettingsStore.getState()._applyTheme();

  // Listen for system theme changes
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    const { theme } = useSettingsStore.getState();
    if (theme === 'system') {
      useSettingsStore.getState()._applyTheme();
    }
  });
}

// Selectors for derived data
export const useSettingsSelectors = () => {
  const store = useSettingsStore();

  return {
    // Theme selectors
    isDarkMode: () => {
      if (store.theme === 'system') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      return store.theme === 'dark';
    },

    // Display selectors
    shouldShowAdvanced: store.showAdvancedOptions,
    shouldAnimateRolls: store.rollAnimations,

    // Auto-save selectors
    isAutoSaveEnabled: store.autoSave,
    autoSaveDelay: store.autoSaveInterval,

    // Storage selectors
    preferredStorageLocation: store.defaultStorageLocation
  };
};
